<template>
  <span
    class="user-avatar"
    :class="{ 'has-img': !!avatarUrl }"
    :style="tileStyle"
    :title="user?.name || ''"
    :aria-label="user?.name || ''"
  >
    <img v-if="avatarUrl" :src="avatarUrl" :alt="user?.name || ''" @error="onImgError" />
    <span v-else class="ini">{{ initials }}</span>
  </span>
</template>

<script>
import { computed, ref } from 'vue'

// Deterministic pastel fallback for people who haven't chosen a colour yet.
const PALETTE = [
  '#59CE7F', '#F5A25D', '#F27D9A', '#7EA6F0', '#9F84E8',
  '#3EB9A6', '#E6B855', '#D66B6B', '#6B93D6', '#B591D6',
]
function hashName(s) {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}
function initialsOf(name) {
  return (name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'
}
// Contrast-safe text colour so light user-picked tints don't leave the initials
// unreadable on a light chip.
function inkFor(hex) {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex || '')) return '#fff'
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq > 175 ? '#0F1420' : '#FFFFFF'
}

export default {
  name: 'UserAvatar',
  props: {
    user:   { type: Object,  default: () => ({}) },
    size:   { type: Number,  default: 28 },
    stroke: { type: Boolean, default: false },
  },
  setup(props) {
    const brokenImg = ref(false)
    const avatarUrl = computed(() => (!brokenImg.value && props.user?.avatar_url) || null)
    const initials  = computed(() => initialsOf(props.user?.name))
    const bg = computed(() => {
      const c = props.user?.avatar_color
      if (c && /^#[0-9a-fA-F]{6}$/.test(c)) return c
      const seed = hashName(props.user?.name || String(props.user?.id ?? ''))
      return PALETTE[seed % PALETTE.length]
    })
    const tileStyle = computed(() => ({
      width:  props.size + 'px',
      height: props.size + 'px',
      fontSize: Math.max(9, Math.round(props.size * 0.4)) + 'px',
      background: bg.value,
      color: inkFor(bg.value),
      boxShadow: props.stroke ? '0 0 0 2px var(--surface, #fff)' : 'none',
    }))
    function onImgError() { brokenImg.value = true }
    return { avatarUrl, initials, tileStyle, onImgError }
  },
}
</script>

<style scoped>
.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 700;
  overflow: hidden;
  flex-shrink: 0;
  line-height: 1;
  user-select: none;
}
.user-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.user-avatar .ini { pointer-events: none; }
</style>
